<template>
    <div class="playlist-card">
        <div class="playlist-card__preview">
            <img :src="safePreview" :alt="title" :title="title" loading="lazy" decoding="async" @error="onPreviewError" />
        </div>
        <div class="playlist-card__actions">
            <p class="playlist-card__title" :title="title">{{ title }}</p>
            <p v-if="downloadError" class="playlist-card__error">{{ downloadError }}</p>
            <div v-if="isReady" class="playlist-quality-group">
                <div class="playlist-quality" :class="{ 'is-open': dropdownOpen }">
                    <button type="button"
                            class="playlist-quality__toggle"
                            @click.stop="toggleDropdown">
                        <span class="playlist-quality__label">
                            <span>{{ selected.ext }}</span>
                            <span>{{ selected.quality }}</span>
                            <span v-if="selected.noAudio" class="playlist-quality__no-audio" title="No audio"><i class="fas fa-volume-mute"></i></span>
                        </span>
                        <svg class="playlist-quality__arrow" width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden="true">
                            <path d="M4 6l4 4 4-4" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
                        </svg>
                    </button>
                    <ul v-if="dropdownOpen" class="playlist-quality__list">
                        <li v-for="format in formats"
                            :key="keyOf(format)"
                            class="playlist-quality__option"
                            @click.stop="selectFormat(format)">
                            <span>{{ format.ext }}</span>
                            <span>{{ format.quality }}</span>
                            <span v-if="format.noAudio" class="playlist-quality__no-audio" title="No audio"><i class="fas fa-volume-mute"></i></span>
                        </li>
                    </ul>
                </div>
                <button type="button"
                        class="playlist-btn playlist-btn--split"
                        :disabled="isDownloading"
                        @click="download">
                    <span>Download</span>
                    <download-icon class="playlist-btn__icon" />
                </button>
            </div>
            <button v-else
                    type="button"
                    class="playlist-btn"
                    :disabled="isLoading"
                    @click="prepare">
                <span v-if="isLoading" class="playlist-btn__spinner"></span>
                <template v-else>
                    <span>{{ buttonLabel }}</span>
                    <download-icon class="playlist-btn__icon" />
                </template>
            </button>
        </div>
    </div>
</template>

<script>
import noImage from '../../img/placeholder/no-image.png';
import { REQUEST_POOL, isAbortError, requestConvert } from '../api/convert';
import {
    NO_FORMATS_REASON,
    buildConvertErrorResult,
    buildNoFormatsResult,
} from '../tools/downloadContracts';
import { dispatchConvertResponse, formatKey, normalizeFormats } from '../tools/formats';
import { safeUrl } from '../tools/sanitize';
import { closeOtherDropdowns, onOutsideClick } from '../tools/dropdowns';
import { DIRECT_DOWNLOAD_DELAY, startDownloadWithReport } from '../tools/downloadActions';
import DownloadIcon from './DownloadIcon';
import downloadAttempts from '../mixins/downloadAttempts';

// состояния карточки — сравниваем с константами, а не со строковыми литералами
const STATE = {
    IDLE: 'idle',
    LOADING: 'loading',
    READY: 'ready',
    ERROR: 'error',
};

// Исходное состояние карточки. Отдельной функцией, потому что в него нужно
// возвращаться и при переиспользовании инстанса под другое видео (watch.source) —
// раньше тот же набор полей перечислялся там во второй раз и мог разойтись
// с data() при добавлении нового поля.
const initialState = () => ({
    state: STATE.IDLE,
    formats: [],
    selected: null,
    dropdownOpen: false,
    // короткая причина вместо общего 'Try again', когда повтор не поможет
    errorLabel: null,
    // вход в воронку уже отправлен. Флагом, а не сравнением с IDLE: карточка может
    // стартовать сразу в ERROR (см. applyInitialFormats), и тогда первый клик по ней —
    // всё ещё первый пользовательский intent, который по IDLE не опознаётся
    funnelEntered: false,
    // отказ именно скачивания: список качеств при этом остаётся на экране
    downloadError: null,
});

// TEA-6094: карточка видео с выбором качества прямо в выдаче — используется
// и для плейлиста (MainResults.vue), и для результатов поиска (SearchResults.vue).
// Ссылка на скачивание готовится здесь, без ухода на главную форму.
export default {
    name: 'PlaylistCard',
    mixins: [downloadAttempts],
    components: { DownloadIcon },
    props: {
        preview: String,
        title: String,
        // ссылка на само видео, по ней запрашиваются форматы
        source: String,
        // метка источника для аналитики (dimension2): карточка живёт в двух разных выдачах,
        // поэтому значение обязательное — забытый prop молча испортил бы раздельный подсчёт
        analyticsSource: {
            type: String,
            required: true,
        },
        // форматы, если они уже пришли вместе с выдачей
        initialFormats: {
            type: Array,
            default: () => [],
        },
    },
    data() {
        return initialState();
    },
    created() {
        this.applyInitialFormats();
    },
    watch: {
        // тот же инстанс может достаться другому видео (список переприсвоен,
        // ключ совпал) — иначе на экране останется выбор качества от прошлого
        source() {
            this.abortRequest();
            Object.assign(this, initialState());
            // initialState() не трогает состояние скачивания — оно живёт в миксине;
            // без сброса карточка под новым видео открылась бы с заблокированной кнопкой
            this.resetDownloadState();
            this.applyInitialFormats();
        },
    },
    mounted() {
        // общий листенер на все карточки, см. tools/dropdowns.js
        this.closeDropdown = () => { this.dropdownOpen = false; };
        this.stopOutsideClick = onOutsideClick(this.closeDropdown);
    },
    beforeDestroy() {
        if (this.stopOutsideClick) {
            this.stopOutsideClick();
        }
        // запрос отменяет хук миксина downloadAttempts: иначе ответ придёт уже после
        // уничтожения карточки и отправит событие в GA
    },
    computed: {
        safePreview() {
            return safeUrl(this.preview) || noImage;
        },
        isReady() {
            return this.state === STATE.READY;
        },
        isLoading() {
            return this.state === STATE.LOADING;
        },
        buttonLabel() {
            if (this.state !== STATE.ERROR) {
                return 'Choose quality';
            }
            return this.errorLabel || 'Try again';
        },
    },
    methods: {
        // Форматы пришли вместе с выдачей — prepare() не вызывается, и событий воронки
        // отсюда не шлём: за многоэлементный ответ уже отчитался MainResults.request(),
        // иначе на один ответ пришлось бы N событий от карточек плюс одно от него.
        applyInitialFormats() {
            const formats = normalizeFormats(this.initialFormats);
            if (!formats.length) {
                // Первый playlist-ответ часто содержит только описания качеств.
                // Оставляем IDLE: первый клик делает второй /api/convert и получает
                // реальные download-ссылки.
                return;
            }

            this.formats = formats;
            this.selected = formats[0];
            this.state = STATE.READY;
        },
        emitFunnelEntry() {
            this.$root.$emit('search-download', this.source, this.analyticsSource);
        },
        emitResults(data) {
            this.$root.$emit('show-results', data, this.source, null, this.analyticsSource);
        },
        onPreviewError(event) {
            event.target.src = noImage;
        },
        // тот же ключ, что и у дедупа в normalizeFormats(): собранный вручную
        // разошёлся бы с formatKey() при первом же изменении его формата
        keyOf(format) {
            return formatKey(format.ext, format.quality, format.noAudio);
        },
        async prepare() {
            if (this.state === STATE.LOADING) {
                return;
            }
            // 'Try again' — тот же пользовательский intent, что и первый клик:
            // повторный эмит задваивал бы вход в воронку и занижал success rate
            if (!this.funnelEntered) {
                this.emitFunnelEntry();
                this.funnelEntered = true;
            }
            this.errorLabel = null;
            this.downloadError = null;
            this.state = STATE.LOADING;
            const controller = this.beginRequest();

            try {
                const data = await requestConvert({
                    link: this.source,
                    subscribeSignedRequestBody: this.$root.subscribeSignedRequestBody,
                    signal: controller.signal,
                    // карточек много — их пул отделён от основной формы
                    pool: REQUEST_POOL.GRID,
                });

                // Карточку успели отдать другому видео: watch.source() уже сбросил
                // состояние под него, но это продолжение осело раньше и записало бы
                // форматы прошлого видео, а emitResults() привязал бы событие не к
                // тому источнику. abort() один этого не ловит — он отклоняет промис
                // микротаской, уже после того как await здесь продолжился.
                if (!this.isCurrentRequest(controller)) {
                    return;
                }

                this.applyPreparedResponse(data);
            } catch (e) {
                // карточку убрали до ответа или она уже занята другим видео —
                // молча выходим, событий слать некому
                if (isAbortError(e) || !this.isCurrentRequest(controller)) {
                    return;
                }
                // try закрывает и разбор ответа, так что сюда может прилететь
                // обычный баг — см. logUnexpectedFailure в миксине
                this.logUnexpectedFailure(e, 'playlist-card');
                this.fail(buildConvertErrorResult(e));
            }
        },
        // Разбор ответа отдельно от старта запроса — как applyResponse() в MainResults,
        // где та же логика разведена на диспетчер и обработчики.
        applyPreparedResponse(data) {
            // ср. ту же проверку в MainResults: без неё нераспарсенный ответ
            // замаскировался бы под «форматов нет»
            // !data, а не data === null: undefined-тело уронило бы data.blacklist ниже
            if (!data) {
                return this.fail(buildNoFormatsResult(null, NO_FORMATS_REASON.EMPTY), 'Bad response');
            }

            if (data.success === false) {
                return this.fail(data, data.message || 'Link not found');
            }

            if (data.blacklist) {
                return this.fail(data);
            }

            // перебор форм — общий с MainResults, см. tools/formats.js: своя копия
            // правил здесь уже расходилась с общей («несколько видео» означало любой
            // массив длиной > 1, а не массив с валидным вторым элементом)
            return dispatchConvertResponse(data, {
                // по ссылке отдали несколько видео — карточка представляет одно,
                // и смешивать форматы нельзя: скачалось бы не то видео.
                // Ср. applyGridResponse() в MainResults, где это разворачивается в список.
                onGrid: () => this.fail(
                    buildNoFormatsResult(data, NO_FORMATS_REASON.MULTIPLE), 'Multiple videos',
                ),
                onSingle: (item) => {
                    const formats = normalizeFormats(item.url);
                    if (!formats.length) {
                        return this.fail(buildNoFormatsResult(data, NO_FORMATS_REASON.EMPTY));
                    }

                    this.formats = formats;
                    this.selected = formats[0];
                    this.state = STATE.READY;
                    this.emitResults(data);
                },
                // пустой список для карточки — это тоже «предложить нечего»
                onEmptyList: () => this.fail(buildNoFormatsResult(data, NO_FORMATS_REASON.EMPTY)),
                onMalformed: () => this.fail(
                    buildNoFormatsResult(data, NO_FORMATS_REASON.MALFORMED), 'Bad response',
                ),
            });
        },
        // Отказ без события аналитики. Отдельно от fail(), потому что отказ, замеченный
        // без собственного запроса (applyInitialFormats), отчитываться не должен:
        // за тот ответ уже отчитался MainResults.request().
        setError(errorLabel = null) {
            this.errorLabel = errorLabel;
            this.state = STATE.ERROR;
        },
        fail(result, errorLabel = null) {
            this.setError(errorLabel);
            this.$root.$emit('show-error', result, this.source, this.analyticsSource);
        },
        toggleDropdown() {
            const next = !this.dropdownOpen;
            // клик по тогглу гасит всплытие, поэтому общий document-листенер соседей
            // не разбудит — закрываем их явно, иначе на экране два раскрытых списка
            if (next) {
                closeOtherDropdowns(this.closeDropdown);
            }
            this.dropdownOpen = next;
        },
        selectFormat(format) {
            this.selected = format;
            this.dropdownOpen = false;
        },
        download() {
            // кнопка заблокирована через :disabled, но состояние может прийти и из
            // программного вызова — повторный вход запустил бы вторую конвертацию
            if (this.isDownloading) {
                return;
            }
            this.dropdownOpen = false;
            this.downloadError = null;
            const ext = this.selected.ext.toLowerCase();
            // карточку может переиспользовать другое видео, пока конвертер работает —
            // поздний отказ должен отчитаться за то видео, которое качали
            const attemptSource = this.source;
            const attemptAnalyticsSource = this.analyticsSource;
            // source не меняется, когда пользователь выбрал другое качество и нажал
            // Download повторно — устаревшую попытку отличает только номер
            const { attempt, finish } = this.beginDownload();

            // TEA-6094: качаем ровно как в одиночном скачивании — конвертируемые форматы
            // показывают модалку прогресса, форматы из раздельных потоков мёржатся
            // на клиенте, и только остальное уходит прямой ссылкой (см. tools/downloadActions.js)
            startDownloadWithReport({
                url: this.selected.url,
                videoObj: this.selected.raw,
                filename: `${this.title}.${ext}`,
                isAudio: ext === 'mp3',
                thumb: this.preview,
                delay: DIRECT_DOWNLOAD_DELAY,
                lateFailureSuffix: 'The converter failed while preparing the file. Pick another quality.',
                // как в MainResults: событие только для прямой ссылки и только если она прошла
                // проверку схемы — у конвертируемых форматов своя аналитика внутри модалки
                beforeDirectDownload: () => {
                    this.$root.$emit('download-click', this.source, ext, this.analyticsSource);
                },
                // состояние READY сохраняем: повтор того же клика ничего не изменит,
                // а список качеств нужен, чтобы пользователь выбрал другой формат.
                // Общая часть отчёта — в миксине.
                onFailure: (reason, suffix) => this.reportDownloadFailure(reason, {
                    attempt,
                    suffix: suffix || 'Pick another quality.',
                    // карточку успели переиспользовать под другое видео
                    stale: attemptSource !== this.source,
                    apply: (message, result) => {
                        this.downloadError = message;
                        this.$root.$emit('show-error', result, attemptSource, attemptAnalyticsSource);
                    },
                }),
                onSettled: finish,
            });
        },
    },
};
</script>
